import { compareEventPosition } from './eventPosition';
import React from 'react';
import Icon from '../components/icon/Icon';
import { LINEUP_LIMIT } from '../config/settings';
import {
    DEFAULT_TIMING,
    elapsedFromMatchStart,
    periodLabel,
    PeriodCount,
    TimingSnapshot,
    timingSnapshotFromMatch,
} from './matchTiming';
import {
    CardTypeEnum,
    CoachRoleEnum,
    CreateEventCommand,
    CreateMatchPlayerCommandOfMatchPlayer,
    EventPeriodEnum,
    EventTypeEnum,
    EventVm,
    ExclusionTypeEnum,
    MatchCoachVm,
    MatchDetailVm,
    MatchStatusEnum,
    PlayerPositionEnum,
    ShotRequest,
    ShotTypeEnum,
    TurnoverTypeEnum,
} from '../utils/api';
import { isInsideGoalMouth } from './goalTargetFrame';

export const getTeamId = (event: EventVm | CreateEventCommand): number | undefined => {
    switch (event.typeId) {
        case EventTypeEnum.Card:
            return event.card?.teamId;
        case EventTypeEnum.Exclusion:
            return event.exclusion?.teamId;
        case EventTypeEnum.Shot:
            return event.shot?.teamId;
        case EventTypeEnum.Swim_Off:
            return event.swimoff?.winnerTeamId;
        case EventTypeEnum.Timeout:
            return event.timeout?.teamId;
        case EventTypeEnum.Challenge:
            return event.challenge?.teamId;
        case EventTypeEnum.Turnover:
            return event.turnover?.teamId;
        case EventTypeEnum.Goalkeeper_Change:
            return event.goalkeeperChange?.teamId;
        default:
            return undefined;
    }
};

export const getPlayerNumber = (event: EventVm): number | undefined => {
    switch (event.typeId) {
        case EventTypeEnum.Card:
            return event.card?.cardedPlayer?.number;
        case EventTypeEnum.Exclusion:
            return event.exclusion?.excludedPlayer?.number;
        case EventTypeEnum.Shot:
            return event.shot?.takenBy?.number;
        case EventTypeEnum.Swim_Off:
            return event.swimoff?.winnerSwimmer?.number;
        case EventTypeEnum.Turnover:
            return event.turnover?.lostPossesionPlayer?.number;
        case EventTypeEnum.Timeout:
        case EventTypeEnum.Challenge:
        default:
            return undefined;
    }
};

export const getPlayerNumberFromPlayerLineup = (
    event: CreateEventCommand | EventVm,
    playerLineup: CreateMatchPlayerCommandOfMatchPlayer[]
): number | undefined => {
    const teamId = getTeamId(event);
    let playerId: number | undefined;
    switch (event.typeId) {
        case EventTypeEnum.Card:
            playerId = event.card?.cardedPlayerId;
            break;
        case EventTypeEnum.Exclusion:
            playerId = event.exclusion?.excludedPlayerId;
            break;
        case EventTypeEnum.Shot:
            playerId = event.shot?.takenById;
            break;
        case EventTypeEnum.Swim_Off:
            playerId = event.swimoff?.winnerSwimmerId;
            break;
        case EventTypeEnum.Turnover:
            playerId = event.turnover?.lostPossesionPlayerId;
            break;
        case EventTypeEnum.Timeout:
        case EventTypeEnum.Challenge:
        default:
            playerId = undefined;
            break;
    }

    const result = playerLineup.find(
        (pl: CreateMatchPlayerCommandOfMatchPlayer): boolean =>
            pl.id === playerId && pl.teamId === teamId
    );
    return result?.number;
};

export const getEventTitle = (event: EventVm | CreateEventCommand): string => {
    switch (event.typeId) {
        case EventTypeEnum.Card: {
            const c = event.card;
            if (c && !c.cardedCoachId && !c.cardedPlayerId) {
                return c.typeId === CardTypeEnum.Red ? 'Team Red Card' : 'Team Yellow Card';
            }
            switch (c?.typeId) {
                case CardTypeEnum.Red:
                    return 'Red Card';
                case CardTypeEnum.Yellow:
                    return 'Yellow Card';
                default:
                    return 'Card';
            }
        }
        case EventTypeEnum.Exclusion:
            return event.exclusion?.typeId
                ? ExclusionTypeEnum[event.exclusion?.typeId].replace('_', ' ')
                : EventTypeEnum[event.typeId].replace('_', ' ');
        case EventTypeEnum.Shot: {
            if (event.shot?.isGoal) {
                return 'Goal';
            } else {
                if (event.shot?.savedById) {
                    return 'Saved Shot';
                } else if (event.shot?.blockedById) {
                    return 'Blocked Shot';
                }
                return 'Missed Shot';
            }
        }
        case EventTypeEnum.Swim_Off:
            return 'Swim Off';
        case EventTypeEnum.Timeout:
            return 'Timeout';
        case EventTypeEnum.Challenge:
            return 'Challenge';
        case EventTypeEnum.Turnover: {
            return 'Turnover';
        }
        default:
            return EventTypeEnum[event.typeId].replace('_', ' ');
    }
};

/**
 * Compact one-liner for the event-submission cloud: "Q2 · 5:43 · White · Goal".
 * Home team = White, away = Blue (water polo cap colors). Segments with no
 * value (e.g. unknown team) are dropped.
 */
export const getEventCloudLabel = (
    event: EventVm | CreateEventCommand,
    match?: MatchDetailVm
): string => {
    // Period labels follow the Match Timing snapshot (1 / H1-H2 / P1-P3 / Q1-Q4), not always Q1-Q4.
    const period = periodLabel(
        event.periodId,
        timingSnapshotFromMatch(match).periodCount
    );
    const time = `${event.minute}:${String(event.seconds).padStart(2, '0')}`;
    const teamId = getTeamId(event);
    let team = '';
    if (match && teamId !== undefined) {
        if (teamId === match.homeTeamId) {
            team = 'White';
        } else if (teamId === match.awayTeamId) {
            team = 'Blue';
        }
    }
    return [period, time, team, getEventTitle(event)].filter(Boolean).join(' · ');
};

export const getEventSubtitle = (
    event: EventVm | CreateEventCommand,
    lineups: CreateMatchPlayerCommandOfMatchPlayer[],
    match?: MatchDetailVm
): string => {
    switch (event.typeId) {
        case EventTypeEnum.Shot: {
            const isFastBreak = event.shot?.isFastBreak;
            const isPenalty =
                event.shot?.typeId === ShotTypeEnum.Penalty ||
                event.shot?.typeId === ShotTypeEnum.Penalty_Shootout;
            let base: string;
            switch (event.shot?.typeId) {
                case ShotTypeEnum.Regular_Attack:
                    base = isFastBreak ? 'Fast Break' : 'Regular Attack';
                    break;
                case ShotTypeEnum.Power_Play:
                    base = 'Power Play';
                    break;
                case ShotTypeEnum.Penalty:
                    base = 'Penalty';
                    break;
                case ShotTypeEnum.Penalty_Shootout:
                    base = 'Penalty Shootout';
                    break;
                default:
                    base = '';
            }
            const parts: string[] = [];
            if (base) {
                parts.push(base);
            }
            const t = event.shot?.shotTechnique;
            // Same values as ShotTechniqueEnum (shotTechniqueEnum.ts): 2=Lob, 3=Skip; literals so log never throws if import breaks
            if (t === 2) {
                parts.push('Lob shot');
            } else if (t === 3) {
                parts.push('Skip shot');
            } else {
                parts.push('Direct shot');
            }
            const fc = event.shot?.fakeCount ?? 0;
            if (!isPenalty && fc > 0) {
                parts.push(fc === 1 ? '1 fake' : '2 fakes');
            }
            return parts.length ? parts.join(' · ') : '';
        }
        case EventTypeEnum.Turnover: {
            const stolenById = event.turnover?.wonPossesionPlayerId;
            const stolenByLineup = lineups.find(
                (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === stolenById
            );

            switch (event.turnover?.typeId) {
                case TurnoverTypeEnum.Lost_Ball:
                    return stolenByLineup
                        ? `Stolen by Player ${stolenByLineup.capNumber || stolenByLineup.number}`
                        : 'Lost Ball';
                case TurnoverTypeEnum.Offensive_Foul:
                    return stolenByLineup
                        ? `Offensive Foul on Player ${
                              stolenByLineup.capNumber || stolenByLineup.number
                          }`
                        : 'Offensive Foul';
                case TurnoverTypeEnum.Ball_Under:
                    return stolenByLineup
                        ? `Submerged by Player ${stolenByLineup.capNumber || stolenByLineup.number}`
                        : 'Ball Under';
                case TurnoverTypeEnum.Clock_Expired:
                    return 'Clock Expired';
                default:
                    return '';
            }
        }
        case EventTypeEnum.Exclusion: {
            const fouledPlayerId = event.exclusion?.fouledPlayerId;
            const fouledLineup = lineups.find(
                (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === fouledPlayerId
            );
            const assistId = event.exclusion?.assistedById;
            const assistLineup = lineups.find(
                (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === assistId
            );
            const parts: string[] = [];
            if (fouledLineup) {
                parts.push(`On Player ${fouledLineup.capNumber || fouledLineup.number}`);
            }
            if (assistLineup && !event.exclusion?.isDoubleExclusion) {
                parts.push(`Assist ${assistLineup.capNumber || assistLineup.number}`);
            }
            return parts.length ? parts.join(' · ') : '';
        }
        case EventTypeEnum.Swim_Off: {
            if (event.swimoff?.winnerSwimmerId) {
                const lostPlayerId =
                    event.swimoff?.winnerSwimmerId === event.swimoff?.homeTeamSwimmerId
                        ? event.swimoff?.awayTeamSwimmerId
                        : event.swimoff?.homeTeamSwimmerId;
                const lostPlayerLineup = lineups.find(
                    (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.id === lostPlayerId
                );
                if (lostPlayerLineup) {
                    return `Won vs Player ${lostPlayerLineup.capNumber || lostPlayerLineup.number}`;
                }
                return '';
            }
            return 'No Winner';
        }
        case EventTypeEnum.Challenge: {
            if (event.challenge?.isSuccessful === true) {
                return 'Successful';
            }
            if (event.challenge?.isSuccessful === false) {
                return 'Unsuccessful';
            }
            return '';
        }
        case EventTypeEnum.Card: {
            const c = event.card;
            if (c && !c.cardedCoachId && !c.cardedPlayerId && match) {
                const tid = c.teamId;
                if (tid === match.homeTeamId) {
                    return match.homeTeamDisplayName ?? '';
                }
                if (tid === match.awayTeamId) {
                    return match.awayTeamDisplayName ?? '';
                }
            }
            return '';
        }
        case EventTypeEnum.Timeout:
        default:
            return '';
    }
};

/** Disciplinary log line for team-only yellow card (append to match disciplinary note). */
export const buildTeamYellowCardDisciplineLine = (event: EventVm, match: MatchDetailVm): string => {
    const teamId = event.card?.teamId;
    const teamName =
        teamId === match.homeTeamId
            ? match.homeTeamDisplayName ?? ''
            : teamId === match.awayTeamId
            ? match.awayTeamDisplayName ?? ''
            : '';
    const q = getQuarterLabelForDiscipline(
        event.periodId,
        timingSnapshotFromMatch(match).periodCount
    );
    const t = formatTime(event.minute, event.seconds);
    return `Yellow card, ${q}, ${t}, ${teamName}`;
};

export function getQuarterLabelForDiscipline(
    periodId: EventPeriodEnum,
    periodCount: PeriodCount = 4
): string {
    // Period labels follow the Match Timing snapshot (1 / H1-H2 / P1-P3 / Q1-Q4), not always "1st quarter".
    return periodLabel(periodId, periodCount) || 'Period';
}

export const getEventIcon = (event: EventVm | CreateEventCommand): React.ReactNode => {
    switch (event.typeId) {
        case EventTypeEnum.Shot:
            return <Icon name="icon-volleyball-ball" />;
        case EventTypeEnum.Card:
            return <Icon name="icon-edit-solid" />;
        case EventTypeEnum.Swim_Off:
            return <Icon name="icon-swimmer" />;
        case EventTypeEnum.Turnover:
            return <Icon name="icon-share" />;
        case EventTypeEnum.Timeout:
            return <Icon name="icon-whistle-solid" />;
        case EventTypeEnum.Challenge:
            return <Icon name="icon-whistle-solid" />;
        case EventTypeEnum.Exclusion: {
            switch (event.exclusion?.typeId) {
                case ExclusionTypeEnum.Exclusion:
                    return <Icon name="icon-times" />;
                case ExclusionTypeEnum.Suspension:
                    return <Icon name="icon-ban" />;
                case ExclusionTypeEnum.Penalty:
                    return <span className="fake-svg"> 5m </span>;
                case ExclusionTypeEnum.Brutality:
                    return <Icon name="icon-user-times" />;
                default:
                    return <Icon name="icon-times" />;
            }
        }
        default:
            return <span className="fake-svg"> </span>;
    }
};

export const getEventComment = (event: EventVm): string => {
    switch (event.typeId) {
        case EventTypeEnum.Challenge:
            return 'CH';
        case EventTypeEnum.Timeout:
            return 'TO';
        case EventTypeEnum.Swim_Off:
            return '#';
        case EventTypeEnum.Card:
            return event?.card?.typeId === CardTypeEnum.Red ? 'RC' : 'YC';
        case EventTypeEnum.Exclusion:
            switch (event.exclusion?.typeId) {
                case ExclusionTypeEnum.Exclusion:
                    return 'E';
                case ExclusionTypeEnum.Suspension:
                    return 'ES';
                case ExclusionTypeEnum.Brutality:
                    return 'EB';
                case ExclusionTypeEnum.Penalty:
                    return 'P';
                default:
                    return '';
            }
        case EventTypeEnum.Shot:
            if (event.shot?.isGoal) {
                switch (event.shot?.typeId) {
                    case ShotTypeEnum.Regular_Attack:
                        return 'G';
                    case ShotTypeEnum.Power_Play:
                        return 'EG';
                    case ShotTypeEnum.Penalty:
                        return 'PG';
                    case ShotTypeEnum.Penalty_Shootout:
                        return 'PSG';
                    default:
                        return '';
                }
            }
            return '';
        default:
            return '';
    }
};

export const compareCoaches = (c1: MatchCoachVm, c2: MatchCoachVm): number => {
    if (
        (c1.roleId === CoachRoleEnum.Head_Coach && c2.roleId === CoachRoleEnum.Head_Coach) ||
        (c1.roleId === CoachRoleEnum.Assistant_Coach && c2.roleId === CoachRoleEnum.Assistant_Coach)
    ) {
        return 0;
    }
    if (c1.roleId === CoachRoleEnum.Head_Coach && c2.roleId === CoachRoleEnum.Assistant_Coach) {
        return -1;
    }
    return 1;
};

export const formatTime = (min: number, sec: number): string =>
    `${min < 10 ? `0${min}` : min}:${sec < 10 ? `0${sec}` : sec}`;

export const filterPeriodEvents = (events: EventVm[], period: EventPeriodEnum): EventVm[] =>
    events.filter((event: EventVm): boolean => event.periodId === period);

export const calculateGoals = (events: EventVm[], period?: EventPeriodEnum): number => {
    const filteredEvents: EventVm[] = [];
    events.forEach((e: EventVm): void => {
        if ((!period || e.periodId === period) && e.shot?.isGoal) {
            filteredEvents.push(e);
        }
    });

    return filteredEvents.length;
};

export const countMissedShots = (events: EventVm[]): number => {
    return events.filter(
        (e: EventVm): boolean => e.typeId === EventTypeEnum.Shot && !e.shot?.isGoal
    ).length;
};

export const getFouls = (events: EventVm[], foulOrdinal: number): string => {
    if (events.length > foulOrdinal) {
        const exclusionType = events[foulOrdinal].exclusion?.type;
        return exclusionType ? exclusionType.charAt(0) : '';
    }
    return '';
};

export const countExclusions = (events: EventVm[]): number => {
    const exclusions = events.filter((e: EventVm): boolean => e.typeId === EventTypeEnum.Exclusion);
    return exclusions.length;
};

/**
 * Same eligibility rule as grayed lineup caps in MatchLineupRow: player may not take part in play
 * after 3+ personal exclusions, or any suspension / brutality recorded against them.
 * Uses `excludedPlayerId` so it stays correct when both teams share cap numbers.
 */
export const isMatchPlayerExcludedFromFieldPlay = (
    events: EventVm[],
    lineupPlayer: CreateMatchPlayerCommandOfMatchPlayer
): boolean => {
    const personalExclusions = events.filter(
        (e: EventVm): boolean =>
            e.typeId === EventTypeEnum.Exclusion &&
            e.exclusion?.excludedPlayerId === lineupPlayer.id
    );
    if (personalExclusions.length > 2) {
        return true;
    }
    if (
        personalExclusions.some(
            (e: EventVm): boolean =>
                e.exclusion?.typeId === ExclusionTypeEnum.Suspension ||
                e.exclusion?.typeId === ExclusionTypeEnum.Brutality
        )
    ) {
        return true;
    }
    return false;
};

/** Lineup cap numbers (`l.number`) excluded from field play for CapSelector etc. */
export const getFieldPlayIneligibleCapNumbers = (
    events: EventVm[],
    teamLineups: CreateMatchPlayerCommandOfMatchPlayer[]
): Set<number> => {
    const caps = new Set<number>();
    for (const l of teamLineups) {
        if (isMatchPlayerExcludedFromFieldPlay(events, l)) {
            caps.add(l.number);
        }
    }
    return caps;
};

/** Roster cap numbers (`l.number`) that are goalkeepers on the defending team. */
export const getGoalkeeperCapNumbers = (
    defendingTeamLineups: CreateMatchPlayerCommandOfMatchPlayer[],
    activeDefendingKeeperCap?: number
): Set<number> => {
    const caps = new Set<number>();
    const fromPosition = defendingTeamLineups.filter(
        (l) => Number(l.positionId) === PlayerPositionEnum.Goalkeeper
    );
    if (fromPosition.length > 0) {
        for (const l of fromPosition) {
            caps.add(l.number);
        }
    } else {
        // Legacy lineups without positionId: match-log defaults GK roster slots to 1 and 13.
        for (const slot of [1, 13]) {
            if (defendingTeamLineups.some((l) => l.number === slot)) {
                caps.add(slot);
            }
        }
    }
    if (activeDefendingKeeperCap != null && activeDefendingKeeperCap > 0) {
        caps.add(activeDefendingKeeperCap);
    }
    return caps;
};

/** Defending field players eligible for "blocked by" — goalkeepers omitted entirely. */
export const filterLineupsForBlockedByPicker = (
    defendingTeamLineups: CreateMatchPlayerCommandOfMatchPlayer[],
    activeDefendingKeeperCap?: number
): CreateMatchPlayerCommandOfMatchPlayer[] => {
    const gkCaps = getGoalkeeperCapNumbers(defendingTeamLineups, activeDefendingKeeperCap);
    return defendingTeamLineups.filter((l) => !gkCaps.has(l.number));
};

export const compareEventsByTime = (e1: EventVm, e2: EventVm): number => {
    if (e1.periodId === e2.periodId) {
        if (e1.minute === e2.minute) {
            if (e1.seconds === e2.seconds) {
                return -compareEventPosition(e1, e2);
            }
            if (e1.seconds > e2.seconds) {
                return 1;
            }
            return -1;
        }
        if (e1.minute > e2.minute) {
            return 1;
        }
        return -1;
    }
    if (e1.periodId > e2.periodId) {
        return 1;
    }
    return -1;
};

/**
 * Strictly after in real match time (higher period wins; countdown clock → lower remaining secs
 * is later; same clock → higher event id wins). Note: [`compareEventsByTime`] sorts low period
 * first globally, so you cannot derive “last play” from sort index alone.
 */
export const isEventChronologicallyAfter = (a: EventVm, b: EventVm): boolean => {
    if (a.periodId !== b.periodId) {
        return a.periodId > b.periodId;
    }
    const secA = a.minute * 60 + a.seconds;
    const secB = b.minute * 60 + b.seconds;
    if (secA !== secB) {
        return secA < secB;
    }
    return a.id > b.id;
};

/** Latest committed event in real game order (same rules as [`isEventChronologicallyAfter`]). */
export const getMostRecentCommittedEvent = (events: EventVm[]): EventVm | undefined => {
    if (!events.length) {
        return undefined;
    }
    return events.reduce(
        (best: EventVm, cur: EventVm): EventVm =>
            isEventChronologicallyAfter(cur, best) ? cur : best
    );
};

/**
 * Opponent penalty situation: either suspension type Penalty, or ordinary Exclusion with
 * "Penalty Excl." (defending foul inside 5 m). Excluded player's team must differ from shooter.
 */
export const isOpposingPenaltyExclusionForShot = (
    event: EventVm,
    shooterTeamId: number
): boolean => {
    const ex = event.exclusion;
    if (event.typeId !== EventTypeEnum.Exclusion || !ex) {
        return false;
    }
    if (ex.teamId === shooterTeamId) {
        return false;
    }
    if (ex.typeId === ExclusionTypeEnum.Penalty) {
        return true;
    }
    return ex.typeId === ExclusionTypeEnum.Exclusion && !!ex.isPenaltyExclusion;
};

/** Committed exclusion/suspension at `e` is strictly before a draft shot at `(shotPeriod, shotMinute, shotSeconds)`. Same-period only meaningful when caller filters periods. Countdown clock: higher remaining = earlier moment. Same second uses id — draft treats as last id. */
export const isCommittedEventBeforeDraftShot = (
    e: EventVm,
    shotPeriod: EventPeriodEnum,
    shotMinute: number,
    shotSeconds: number
): boolean => {
    if (e.periodId !== shotPeriod) {
        return false;
    }
    const eSec = e.minute * 60 + e.seconds;
    const sSec = shotMinute * 60 + shotSeconds;
    if (eSec !== sSec) {
        return eSec > sSec;
    }
    return e.id < Number.MAX_SAFE_INTEGER;
};

/** Opposing player Exclusion / Suspension that can anchor a power-play shot (not coach, penalty, brutality, or double). */
const isPowerPlayEligibleExclusion = (event: EventVm): boolean => {
    const ex = event.exclusion;
    if (!ex || event.typeId !== EventTypeEnum.Exclusion) {
        return false;
    }
    if (!ex.excludedPlayerId) {
        return false;
    }
    if (ex.excludedCoachId) {
        return false;
    }
    const t = ex.typeId;
    if (t === ExclusionTypeEnum.Penalty || t === ExclusionTypeEnum.Brutality) {
        return false;
    }
    return t === ExclusionTypeEnum.Exclusion || t === ExclusionTypeEnum.Suspension;
};

/** These do not change possession, so they do not end an 18-second power play. */
const doesNotEndPowerPlay = (event: EventVm): boolean =>
    event.typeId === EventTypeEnum.Timeout ||
    event.typeId === EventTypeEnum.Challenge ||
    event.typeId === EventTypeEnum.Card ||
    event.typeId === EventTypeEnum.Goalkeeper_Change;

/**
 * Latest possession-relevant event whose primary team is `teamId`, strictly before the draft shot.
 * Timeout, challenge, card, and goalkeeper change are skipped.
 */
export const findLatestEventForTeamBeforeDraftShot = (
    events: EventVm[],
    teamId: number,
    shotPeriod: EventPeriodEnum,
    shotMinute: number,
    shotSeconds: number
): EventVm | undefined => {
    const shotDraft = {
        id: Number.MAX_SAFE_INTEGER,
        periodId: shotPeriod,
        minute: shotMinute,
        seconds: shotSeconds,
    } as EventVm;
    const candidates = events.filter((e: EventVm): boolean => {
        if (doesNotEndPowerPlay(e)) {
            return false;
        }
        const tid = getTeamId(e);
        if (tid !== teamId) {
            return false;
        }
        return isEventChronologicallyAfter(shotDraft, e);
    });
    if (!candidates.length) {
        return undefined;
    }
    return candidates.reduce(
        (best: EventVm, cur: EventVm): EventVm =>
            isEventChronologicallyAfter(cur, best) ? cur : best
    );
};

/** Ordinary field exclusion / suspension only: not suspension Penalty/Brutality, not double, not penalty-exclusion (5 m foul). */
const isRegularPowerPlayExclusionForShortWindow = (event: EventVm): boolean => {
    if (!isPowerPlayEligibleExclusion(event)) {
        return false;
    }
    const ex = event.exclusion!;
    if (ex.isDoubleExclusion || ex.isPenaltyExclusion) {
        return false;
    }
    return true;
};

/**
 * Latest committed opposing player exclusion/suspension (same quarter) chronologically before the draft shot
 * that can serve as power-play anchor; skips double exclusions.
 */
export const findOpposingPowerPlayAnchor = (
    events: EventVm[],
    shooterTeamId: number,
    shotPeriod: EventPeriodEnum,
    shotMinute: number,
    shotSeconds: number
): EventVm | undefined => {
    const candidates = events.filter((e: EventVm): boolean => {
        if (!isPowerPlayEligibleExclusion(e)) {
            return false;
        }
        const ex = e.exclusion!;
        if (ex.teamId === shooterTeamId || ex.isDoubleExclusion) {
            return false;
        }
        if (e.periodId !== shotPeriod) {
            return false;
        }
        return isCommittedEventBeforeDraftShot(e, shotPeriod, shotMinute, shotSeconds);
    });
    if (!candidates.length) {
        return undefined;
    }
    return candidates.reduce(
        (best: EventVm, cur: EventVm): EventVm =>
            isEventChronologicallyAfter(cur, best) ? cur : best
    );
};

export const getSuggestedPowerPlayShotTypeIfEligible = (
    events: EventVm[],
    shooterTeamId: number,
    opponentTeamId: number,
    shotPeriod: EventPeriodEnum,
    shotMinute: number,
    shotSeconds: number,
    timing: TimingSnapshot = DEFAULT_TIMING
): ShotTypeEnum | undefined => {
    const latestOpp = findLatestEventForTeamBeforeDraftShot(
        events,
        opponentTeamId,
        shotPeriod,
        shotMinute,
        shotSeconds
    );
    if (!latestOpp || !isRegularPowerPlayExclusionForShortWindow(latestOpp)) {
        return undefined;
    }
    const ex = latestOpp.exclusion!;
    if (ex.teamId === shooterTeamId) {
        return undefined;
    }
    if (latestOpp.periodId !== shotPeriod) {
        return undefined;
    }
    if (!isCommittedEventBeforeDraftShot(latestOpp, shotPeriod, shotMinute, shotSeconds)) {
        return undefined;
    }
    const eSec = latestOpp.minute * 60 + latestOpp.seconds;
    const sSec = shotMinute * 60 + shotSeconds;
    const delta = eSec - sSec;
    if (delta < 0 || delta > timing.exclusionDurationSeconds) {
        return undefined;
    }
    const goalEndsIt = events.some((event) => {
        if (event.typeId !== EventTypeEnum.Shot || !event.shot?.isGoal) {
            return false;
        }
        if (!isEventChronologicallyAfter(event, latestOpp)) {
            return false;
        }
        return isCommittedEventBeforeDraftShot(event, shotPeriod, shotMinute, shotSeconds);
    });
    if (goalEndsIt) {
        return undefined;
    }
    return ShotTypeEnum.Power_Play;
};

/** Elapsed game time from nominal match start (each Period = Period length countdown). Cross-Period monotonic. */
export const absoluteGameElapsedSecondsFromMatchStart = (
    periodId: EventPeriodEnum,
    minute: number,
    seconds: number,
    timing: TimingSnapshot = DEFAULT_TIMING
): number =>
    elapsedFromMatchStart(
        periodId,
        minute,
        seconds,
        timing.periodLengthMinutes,
        timing.periodCount
    );

const isOpposingPlayerBrutalityExclusion = (e: EventVm, shooterTeamId: number): boolean => {
    const ex = e.exclusion;
    if (e.typeId !== EventTypeEnum.Exclusion || !ex) {
        return false;
    }
    if (ex.typeId !== ExclusionTypeEnum.Brutality) {
        return false;
    }
    if (ex.teamId === shooterTeamId) {
        return false;
    }
    if (!ex.excludedPlayerId || ex.excludedCoachId) {
        return false;
    }
    return true;
};

/**
 * Opposing Brutality strictly before draft shot; latest such event (anchor for 240s window).
 */
export const findLatestOpposingBrutalityBeforeShot = (
    events: EventVm[],
    shooterTeamId: number,
    shotPeriod: EventPeriodEnum,
    shotMinute: number,
    shotSeconds: number
): EventVm | undefined => {
    const shotDraft = {
        id: Number.MAX_SAFE_INTEGER,
        periodId: shotPeriod,
        minute: shotMinute,
        seconds: shotSeconds,
    } as EventVm;
    const brutalities = events.filter(
        (e: EventVm): boolean =>
            isOpposingPlayerBrutalityExclusion(e, shooterTeamId) &&
            isEventChronologicallyAfter(shotDraft, e)
    );
    if (!brutalities.length) {
        return undefined;
    }
    return brutalities.reduce(
        (best: EventVm, cur: EventVm): EventVm =>
            isEventChronologicallyAfter(cur, best) ? cur : best
    );
};

export const getSuggestedPowerPlayFromBrutality240IfEligible = (
    events: EventVm[],
    shooterTeamId: number,
    shotPeriod: EventPeriodEnum,
    shotMinute: number,
    shotSeconds: number,
    timing: TimingSnapshot = DEFAULT_TIMING
): ShotTypeEnum | undefined => {
    const anchor = findLatestOpposingBrutalityBeforeShot(
        events,
        shooterTeamId,
        shotPeriod,
        shotMinute,
        shotSeconds
    );
    if (!anchor) {
        return undefined;
    }
    const shotElapsed = absoluteGameElapsedSecondsFromMatchStart(
        shotPeriod,
        shotMinute,
        shotSeconds,
        timing
    );
    const anchorElapsed = absoluteGameElapsedSecondsFromMatchStart(
        anchor.periodId,
        anchor.minute,
        anchor.seconds,
        timing
    );
    const delta = shotElapsed - anchorElapsed;
    if (delta < 0 || delta > timing.brutalityDurationSeconds) {
        return undefined;
    }
    return ShotTypeEnum.Power_Play;
};

export const refreshMatchGoals = (match: MatchDetailVm, result: EventVm) => {
    return {
        ...match,
        homeTeamGoalsQ1: result.match.homeTeamGoalsQ1,
        homeTeamGoalsQ2: result.match.homeTeamGoalsQ2,
        homeTeamGoalsQ3: result.match.homeTeamGoalsQ3,
        homeTeamGoalsQ4: result.match.homeTeamGoalsQ4,
        homeTeamGoalsOT: result.match.homeTeamGoalsOT,
        awayTeamGoalsQ1: result.match.awayTeamGoalsQ1,
        awayTeamGoalsQ2: result.match.awayTeamGoalsQ2,
        awayTeamGoalsQ3: result.match.awayTeamGoalsQ3,
        awayTeamGoalsQ4: result.match.awayTeamGoalsQ4,
        awayTeamGoalsOT: result.match.awayTeamGoalsOT,
        homeTeamGoalsTotal: result.match.homeTeamGoalsTotal,
        awayTeamGoalsTotal: result.match.awayTeamGoalsTotal,
    } as MatchDetailVm;
};

export const calculateGoalsAfterEventChange = (
    homeEvents: EventVm[],
    awayEvents: EventVm[],
    match: MatchDetailVm
): MatchDetailVm => {
    return {
        ...match,
        homeTeamGoalsQ1: calculateGoals(homeEvents, EventPeriodEnum.First_Period),
        homeTeamGoalsQ2: calculateGoals(homeEvents, EventPeriodEnum.Second_Period),
        homeTeamGoalsQ3: calculateGoals(homeEvents, EventPeriodEnum.Third_Period),
        homeTeamGoalsQ4: calculateGoals(homeEvents, EventPeriodEnum.Fourth_Period),
        homeTeamGoalsOT: calculateGoals(homeEvents, EventPeriodEnum.Overtime),
        homeTeamGoalsTotal: calculateGoals(homeEvents),
        awayTeamGoalsQ1: calculateGoals(awayEvents, EventPeriodEnum.First_Period),
        awayTeamGoalsQ2: calculateGoals(awayEvents, EventPeriodEnum.Second_Period),
        awayTeamGoalsQ3: calculateGoals(awayEvents, EventPeriodEnum.Third_Period),
        awayTeamGoalsQ4: calculateGoals(awayEvents, EventPeriodEnum.Fourth_Period),
        awayTeamGoalsOT: calculateGoals(awayEvents, EventPeriodEnum.Overtime),
        awayTeamGoalsTotal: calculateGoals(awayEvents),
    } as MatchDetailVm;
};

export const prepareMatchRequestForSwimoffEvent = (
    event: EventVm,
    match: MatchDetailVm
): MatchDetailVm => {
    return {
        ...match,
        status: getMatchStatusByEventPeriod(event.periodId),
        statusId: getMatchStatusIdByEventPeriod(event.periodId),
        homeTeamGoalsQ1:
            event.periodId === EventPeriodEnum.First_Period ? 0 : match.homeTeamGoalsQ1,
        homeTeamGoalsQ2:
            event.periodId === EventPeriodEnum.Second_Period ? 0 : match.homeTeamGoalsQ2,
        homeTeamGoalsQ3:
            event.periodId === EventPeriodEnum.Third_Period ? 0 : match.homeTeamGoalsQ3,
        homeTeamGoalsQ4:
            event.periodId === EventPeriodEnum.Fourth_Period ? 0 : match.homeTeamGoalsQ4,
        homeTeamGoalsOT: event.periodId === EventPeriodEnum.Overtime ? 0 : match.homeTeamGoalsOT,
        awayTeamGoalsQ1:
            event.periodId === EventPeriodEnum.First_Period ? 0 : match.awayTeamGoalsQ1,
        awayTeamGoalsQ2:
            event.periodId === EventPeriodEnum.First_Period ? 0 : match.awayTeamGoalsQ2,
        awayTeamGoalsQ3:
            event.periodId === EventPeriodEnum.First_Period ? 0 : match.awayTeamGoalsQ3,
        awayTeamGoalsQ4:
            event.periodId === EventPeriodEnum.First_Period ? 0 : match.awayTeamGoalsQ4,
        awayTeamGoalsOT:
            event.periodId === EventPeriodEnum.First_Period ? 0 : match.awayTeamGoalsOT,
    } as MatchDetailVm;
};

export const getMatchStatusIdByEventPeriod = (period: EventPeriodEnum): MatchStatusEnum => {
    switch (period) {
        case EventPeriodEnum.First_Period:
            return MatchStatusEnum.First_Period;
        case EventPeriodEnum.Second_Period:
            return MatchStatusEnum.Second_Period;
        case EventPeriodEnum.Third_Period:
            return MatchStatusEnum.Third_Period;
        case EventPeriodEnum.Fourth_Period:
            return MatchStatusEnum.Fourth_Period;
        case EventPeriodEnum.Overtime:
            return MatchStatusEnum.Overtime;
        default:
            return MatchStatusEnum.First_Period;
    }
};

export const getMatchStatusByEventPeriod = (period: EventPeriodEnum): string => {
    switch (period) {
        case EventPeriodEnum.First_Period:
            return '1st Period';
        case EventPeriodEnum.Second_Period:
            return '2nd Period';
        case EventPeriodEnum.Third_Period:
            return '3rd Period';
        case EventPeriodEnum.Fourth_Period:
            return '4th Period';
        case EventPeriodEnum.Overtime:
            return 'Overtime';
        default:
            return '1st Period';
    }
};

export const lineupNumberHasEvents = (
    events: EventVm[],
    capNumber: number,
    teamId: number
): boolean => {
    let isUsed = false;

    // eslint-disable-next-line array-callback-return
    events.map((event: EventVm): void => {
        switch (event.typeId) {
            case EventTypeEnum.Card:
                if (
                    event.card?.cardedPlayer?.number === capNumber &&
                    event.card?.cardedPlayer.teamId === teamId
                ) {
                    isUsed = true;
                }
                break;
            case EventTypeEnum.Exclusion:
                if (
                    event.exclusion?.excludedPlayer?.number === capNumber &&
                    event.exclusion?.excludedPlayer?.teamId === teamId
                ) {
                    isUsed = true;
                }
                if (
                    event.exclusion?.fouledPlayer?.number === capNumber &&
                    event.exclusion?.fouledPlayer?.teamId === teamId
                ) {
                    isUsed = true;
                }
                break;
            case EventTypeEnum.Shot:
                if (
                    event.shot?.assistedBy?.number === capNumber &&
                    event.shot?.assistedBy?.teamId === teamId
                ) {
                    isUsed = true;
                }
                if (
                    event.shot?.blockedBy?.number === capNumber &&
                    event.shot?.blockedBy?.teamId === teamId
                ) {
                    isUsed = true;
                }
                if (
                    event.shot?.savedBy?.number === capNumber &&
                    event.shot?.savedBy?.teamId === teamId
                ) {
                    isUsed = true;
                }
                if (
                    event.shot?.takenBy?.number === capNumber &&
                    event.shot?.takenBy?.teamId === teamId
                ) {
                    isUsed = true;
                }
                break;
            case EventTypeEnum.Swim_Off:
                if (
                    event.swimoff?.homeTeamSwimmer?.number === capNumber &&
                    event.swimoff?.homeTeamSwimmer?.teamId === teamId
                ) {
                    isUsed = true;
                }
                if (
                    event.swimoff?.awayTeamSwimmer?.number === capNumber &&
                    event.swimoff?.awayTeamSwimmer?.teamId === teamId
                ) {
                    isUsed = true;
                }
                // winner check is redundant
                if (
                    event.swimoff?.winnerSwimmer?.number === capNumber &&
                    event.swimoff?.winnerSwimmer?.teamId === teamId
                ) {
                    isUsed = true;
                }
                break;
            case EventTypeEnum.Turnover:
                if (
                    event.turnover?.lostPossesionPlayer?.number === capNumber &&
                    event.turnover?.lostPossesionPlayer?.teamId === teamId
                ) {
                    isUsed = true;
                }
                if (
                    event.turnover?.wonPossesionPlayer?.number === capNumber &&
                    event.turnover?.wonPossesionPlayer?.teamId === teamId
                ) {
                    isUsed = true;
                }
                break;
            case EventTypeEnum.Timeout:
            case EventTypeEnum.Challenge:
            default:
                break;
        }
    });

    return isUsed;
};

export const getListOfDeletedLineupNumberWithEvent = (
    events: EventVm[],
    teamId: number,
    lineups: CreateMatchPlayerCommandOfMatchPlayer[]
): number[] => {
    const deletedLineupNumbersWithEvents: number[] = [];

    for (let i = 0; i < LINEUP_LIMIT; i++) {
        if (lineupNumberHasEvents(events, i + 1, teamId)) {
            const findResult = lineups.find(
                (l: CreateMatchPlayerCommandOfMatchPlayer): boolean => l.number === i + 1
            );
            if (!findResult) {
                deletedLineupNumbersWithEvents.push(i + 1);
            }
        }
    }

    return deletedLineupNumbersWithEvents;
};

export const isShotOnTarget = (shot: ShotRequest): boolean =>
    isInsideGoalMouth(shot.targetX, shot.targetY);
